import i18next from "i18next";

import { formatShortDateWithWeekday } from "@ergon-ai/utils";

interface CivilDay {
  year: number;
  month: number;
  day: number;
}

/**
 * The calendar day an instant falls on, in `timeZone` or the host zone when
 * absent. Read from `Intl` parts, not `getFullYear()`, so the caller can ask
 * for a zone the device is not in.
 */
const civilDay = (date: Date, timeZone?: string): CivilDay => {
  const parts: Partial<CivilDay> = {};
  for (const part of new Intl.DateTimeFormat("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  }).formatToParts(date)) {
    if (part.type === "year") parts.year = Number(part.value);
    if (part.type === "month") parts.month = Number(part.value);
    if (part.type === "day") parts.day = Number(part.value);
  }
  return {
    year: parts.year ?? NaN,
    month: parts.month ?? NaN,
    day: parts.day ?? NaN,
  };
};

const dayKey = ({ year, month, day }: CivilDay) => `${year}-${month}-${day}`;

/** The day before, as calendar arithmetic in UTC: no DST hour to trip over. */
const previousDayKey = ({ year, month, day }: CivilDay) => {
  const cursor = new Date(Date.UTC(year, month - 1, day));
  cursor.setUTCDate(cursor.getUTCDate() - 1);
  return `${cursor.getUTCFullYear()}-${cursor.getUTCMonth() + 1}-${cursor.getUTCDate()}`;
};

/**
 * Formats a date for display in chat date separators (no time).
 *
 * - Returns `"Today"` if the date is the current calendar day.
 * - Returns `"Yesterday"` if the date is the previous calendar day.
 * - Returns `"Monday 27/01/25"` (weekday + short date) for older dates.
 *
 * @param timeZone - Optional IANA zone. Omitted means the device's own zone,
 *   which every native caller wants. A server-rendered caller passes one so the
 *   HTML does not depend on the host zone. `Intl` rejects an unknown zone, so
 *   pass a resolved one (`Intl.DateTimeFormat().resolvedOptions().timeZone`).
 */
export const getChatDateLabel = (
  date: Date | string,
  locale?: string,
  // The web has no i18next instance; it hands in next-intl's lookup instead.
  translate: (key: string, locale: string) => string = (key, lng) =>
    i18next.t(key, { lng }),
  timeZone?: string,
): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const resolvedLocale = locale ?? i18next.language;

  const today = civilDay(new Date(), timeZone);
  const inputDay = dayKey(civilDay(dateObj, timeZone));

  if (inputDay === dayKey(today)) {
    // `lng` explicitly: the caller passed a locale, so the relative word must
    // come from that language too, not from the ambient instance (EAH-2135).
    return translate("date.today", resolvedLocale);
  }

  if (inputDay === previousDayKey(today)) {
    return translate("date.yesterday", resolvedLocale);
  }

  return formatShortDateWithWeekday(dateObj, resolvedLocale, timeZone);
};
